<template>
    <div class="page container" data-page="settings">
        <div class="pure-g">

            <div class="pure-u-1">
                <h1 class="title">Settings</h1>
            </div>
            
            <div class="pure-u-1 pure-u-lg-5-24 left">
                <ul class="tabLinks">
                    <li v-for="(tab, index) in allTabs" :key="'tab_'+index">
                        <a href="#0"
                            @click.prevent="changeTab(tab.name)"
                            :class="{active: currentTab === tab.name}"
                        >
                            {{ tab.title }}
                        </a>
                    </li>
                </ul>
            </div>

            <div class="pure-u-1 pure-u-lg-14-24 right">
                
                <div class="tabs">

                    <!--Personal Info-->
                    <div class="tab" v-show="currentTab === 'personal'">
                        <div class="section">

                            <h3 class="section_title">Personal Information</h3>
                            
                            <form ref="personal_info_form" v-if="userData !== null">
                                
                                <ion-icon name="person"></ion-icon>

                                <label>First Name:</label>
                                <input type="text" v-model="userData.firstName" :class="{error: userData.firstName === null || userData.firstName === ''}" />
                                
                                <label>Last Name:</label>
                                <input type="text" v-model="userData.lastName" :class="{error: userData.lastName === null || userData.lastName === ''}" />
                                
                                <br>
                                <input type="submit" @click.prevent="updateProfile()" class="btn green" value="Update" />
                            </form>

                        </div>
                    </div>
                    
                    <!--Notifications-->
                    <div class="tab" v-show="currentTab === 'notifications'">
                        <div class="section">

                            <h3 class="section_title">Notifications</h3>
                            <p class="section_desc">Add your email and phone number to receive important event notifications from us.</p>

                            <div v-if="userData !== null" class="block m-bottom">
                                <form ref="email_form" class="b-bottom">
                                    
                                    <ion-icon name="mail"></ion-icon>
                                    <h4>Event Notifications via Email:</h4>

                                    <label class="switch">
                                        <span>Off</span>
                                        <input type="checkbox" v-model="userData.settings.sendEmail" @change.prevent="updateNotification('email')">
                                        <i></i>
                                        <span>On</span>
                                    </label>

                                    <div v-show="userData.settings.sendEmail && !validEmail">
                                        <label>Email Address:</label>
                                        <input type="text" 
                                            v-model="userData.email"
                                            :disabled="validEmail"
                                            placeholder="Email Address"
                                            :class="{error: !validEmail}" 
                                        />
                                        <input type="submit" 
                                            value="Update Email"
                                            class="btn green"
                                            @click.prevent="updateNotification('email')"
                                        >
                                    </div>
                                </form>
                            </div>

                            <div v-if="userData !== null" class="block">
                                <form ref="phone_form">
                                    
                                    <ion-icon name="call"></ion-icon>
                                    <h4>Event Notifications via Text Message:</h4>
                                    
                                    <label class="switch">
                                        <span>Off</span>
                                        <input type="checkbox" v-model="userData.settings.sendTxt" @change.prevent="updateNotification('phone')">
                                        <i></i>
                                        <span>On</span>
                                    </label>

                                    <div v-show="userData.settings.sendTxt">
                                        <label>Phone number: </label>
                                        <input type="tel" 
                                            v-model="userData.phoneNumber"
                                            maxlength="10"
                                            placeholder="Phone Number" 
                                            :class="{error: !validPhone}"
                                        />
                                        <input type="submit" 
                                            value="Update Phone Number"
                                            class="btn green"
                                            @click.prevent="updateNotification('phone')"
                                        >
                                        <br/><br/>
                                        <!-- <a href="#0" class="btn green" @click.prevent="sendTestMessage()" :disabled="loading">Test Message</a> -->
                                    </div>
                                </form>
                            </div>

                        </div>
                    </div>

                    <!--Payment History-->
                    <div class="tab" v-show="currentTab === 'payment_history'">
                        <div class="section">

                            <h3 class="section_title">Payment History</h3>
                            <p class="section_desc">All your transactions are saved here for your records.</p>

                            <div class="transactions" v-if="transactions.length > 0">
                                <div v-for="(trans, index) in transactions" :key="'tran_'+index" class="transaction">
                                    <h4>{{ trans.payLabel }}</h4>
                                    <p>
                                        <span class="price">{{ trans.payAmountText }}</span> | 
                                        <span class="type">{{ trans.type }}</span> | 
                                        <span class="time">{{ trans.created | moment('DD MMM, YYYY - h:mm a') }}</span>
                                    </p>
                                </div>
                            </div>
                            <div v-else>
                                <p class="empty">No transactions made at the moment.</p>
                            </div>

                        </div>
                    </div>

                    <!--Saved Cards-->
                    <div class="tab" v-show="currentTab === 'saved_cards'">
                        <div v-if="wallet !== null" class="section">

                            <h3 class="section_title">Saved Cards / Wallet</h3>
                            <p class="section_desc">All your saved cards are to view here. We don't save your credit card information. It is securely saved by our payment gateway partner (Stripe).</p>

                            <div class="wallet">
                                <div v-if="wallet.card !== null" class="card">
                                    <img src="../../assets/img/cards/visa.png" height="25" alt="Visa" />
                                    <div class="card_wrapper">
                                        <p class="card_type"><span>{{ wallet.card.brand }}</span>•••• {{ wallet.card.last4 }}</p>
                                        <p class="card_exp">Expires {{ wallet.card.exp_month }} / {{ wallet.card.exp_year }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <h3>Wallet</h3>
                            <p class="empty">No saved cards.</p>
                        </div>
                    </div>

                    <!--Address & Location-->
                    <div class="tab" v-show="currentTab === 'address_location'">
                        
                        <div class="location section">

                            <h3 class="section_title">Location</h3>
                            <p class="section_desc">You can change your default location to appear in that current city as a member. Please be accurate. Thank you.</p>
                            
                            <!--Location-->
                            <div v-if="location !== null">
                                <div class="location block">
                                    <ion-icon name="navigate"></ion-icon>
                                    <h4>
                                        Default Location: <br>
                                        <span>{{ location.city.name }}, {{ location.state.name }}</span>
                                    </h4>

                                    <a v-if="changeLocationInit === null" href="#0" class="btn green" @click.prevent="toggleLocation">Change Default Location</a>
                                    <form v-else class="formArea">
                                        <div>
                                            <select v-if="!loading" v-model="userCountry" @change.prevent="toggleCountry">
                                                <option value="">-- Select Country --</option>
                                                <option value="CA">Canada</option>
                                                <option value="US">United States</option>
                                            </select>
                                            
                                            <select v-if="!loading && allStates !== null && userCountry !== ''" v-model="userState" @change.prevent="toggleStates">
                                                <option value="">-- Select State --</option>
                                                <option v-for="(state, index) in allStates" :key="index" :value="state.id">{{ state.name }}</option>
                                            </select>

                                            <select v-if="!loading && allCities !== null && userState !== ''" v-model="userCity">
                                                <option value="">-- Select City --</option>
                                                <option v-for="(city, index) in allCities" :key="index" :value="city.id">{{ city.name }}</option>
                                            </select>
                                            
                                            <input v-if="!loading && userCountry !== '' && userState !== '' && userCity !== ''" @click.prevent="updateLocation" type="submit" value="Update Location" class="btn green" />
                                            
                                            <div v-if="loading" class="loading small no-margin"></div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            <div v-else>
                                <p class="empty">No saved locations</p>
                                <a href="#0" class="btn green">Set Default Location</a>
                            </div>
                        </div>

                        <!--Address-->
                        <div class="address section">

                            <h3 class="section_title">Address</h3>
                            <p class="section_desc">Your address will appear here when you use our Eats section.</p>

                            <div v-if="address !== null">
                                <div class="address block">
                                    <ion-icon name="location"></ion-icon>
                                    <h4>
                                        Delivery Address: <br>
                                        <span>{{ address.street }}, {{ address.city }}, {{ address.state }}, {{ address.country }} {{ address.postalCode }}</span>
                                    </h4>
                                    <!-- <a href="#0" class="btn green">Change Delivery Address</a> -->
                                </div>
                            </div>
                            <div v-else>
                                <p class="empty">No saved addresses.</p>
                            </div>
                        </div>

                    </div>

                </div>

            </div>

            <div class="pure-u-1 pure-u-lg-5-24"></div>
        </div>
    </div>
</template>

<script>
import countryData from 'country-state-city'
const fb = require('../../firebaseConfig.js')

export default {
    data(){
        return{
            currentTab: 'notifications',
            allTabs: [
                {
                    name: 'notifications',
                    title: 'Notifications'
                },
                {
                    name: 'payment_history',
                    title: 'Payment History'
                },
                {
                    name: 'saved_cards',
                    title: 'Saved Cards & Wallet'
                },
                {
                    name: 'address_location',
                    title: 'Address & Location'
                }
            ],
            userData: null,
            validEmail: false,
            validPhone: false,
            loading: false,
            transactions: [],
            wallet: null,
            allLocations: [],
            location: null,
            changeLocationInit: null,
            address: null,
            userCountry: '',
            userState: '',
            userCity: '',
            allStates: null,
            allCities: null
        }
    },
    mounted(){

        let self = this

        this.allLocations = this.$store.state.locations

        if(this.$store.state.userProfile !== null){

            this.userData = this.$store.state.userProfile

            if(this.userData.email !== null){
                this.validEmail = true
            }
            if(this.userData.phoneNumber !== null){
                this.validPhone = true
            }

            this.wallet = this.$store.state.userProfile.wallet

            console.log('Wallet: ', this.wallet)

            this.location = this.$store.state.userProfile.location
            this.address = this.$store.state.userProfile.address
        }

        if(this.$store.currentUser !== null){
            fb.usersCollection.doc(this.$store.state.currentUser.uid).collection('transactions').get().then(snap => {
                
                let currentTransactions = []

                snap.forEach(function(transaction){
                    currentTransactions.push(transaction.data())
                })

                currentTransactions = currentTransactions.reverse()

                self.transactions = currentTransactions
                currentTransactions = []
            })
        }
    },
    methods: {
        changeTab(type){
            this.currentTab = type
        },
        updateProfile(){

            console.debug('Update profile: ', this.userData)

            let updates = {}

            if(this.userData.firstName !== null && this.userData.firstName !== ''){
                updates.firstName = this.userData.firstName
            }
            if(this.userData.lastName !== null && this.userData.lastName !== ''){
                updates.lastName = this.userData.lastName
            }
            if(this.userData.firstName && this.userData.lastName){
                updates.displayName = this.userData.firstName + ' ' + this.userData.lastName
            }
        },
        
        // Notification Settings
        updateNotification(type){

            let userRef = fb.usersCollection.doc(this.$store.state.currentUser.uid)
            
            if(type === 'email'){
                
                if(this.userData.email !== null && this.userData.email !== ''){
                    this.validEmail = true
                }else{
                    this.validEmail = false
                }

                if(this.validEmail){
                    
                    let updates = {
                        settings: this.userData.settings
                    }
                    if(this.userData.email !== null && this.userData.email !== ''){
                        updates.email = this.userData.email
                    }

                    console.debug('Update email: ', updates)
                    
                    userRef.update(updates).then(() => {
                        
                        console.log('Successfully updated notifications!')

                    }).catch(err => console.log('Error updating: ', err.message))
                }

            }else if(type === 'phone'){

                if(this.userData.phoneNumber !== null && this.userData.phoneNumber !== ''){
                    this.validPhone = true
                }else{
                    this.validPhone = false
                }

                if(this.validPhone){

                    let updates = {
                        settings: this.userData.settings
                    }
                    if(this.userData.phoneNumber !== null && this.userData.phoneNumber !== ''){
                        updates.phoneNumber = this.userData.phoneNumber
                    }
                    
                    console.debug('Update phone: ', updates)
                    
                    userRef.update(updates).then(() => {
                        
                        console.log('Successfully updated notifications!')

                    }).catch(err => console.log('Error updating: ', err.message))
                }
            }
        },
        sendTestMessage(){

            let self = this

            self.loading = true

            if(this.userData.phoneNumber !== null){
                
                self.$http.post('https://us-central1-vanmgl.cloudfunctions.net/SendTxt', {
                    phoneNumber: self.userData.phoneNumber,
                    message: 'Sending test message from OneMgl'
                }).then(response => {
                    if(response.status === 200){

                        console.log('Successfully sent a test message')
                        self.loading = false
                    }
                })
                .catch((err) => {
                    console.log('Error sending test message ', err.message)
                    self.loading = false
                })
            }else{
                self.loading = false
            }
        },

        // Location Settings
        toggleCountry(){
            if(this.userCountry === 'CA'){
                this.allStates = countryData.getStatesOfCountry('38')
                this.userState = ''
                this.userCity = ''
            }else if(this.userCountry === 'US'){
                this.allStates = countryData.getStatesOfCountry('231')
                this.userState = ''
                this.userCity = ''
            }
        },
        toggleStates(){
            this.allCities = countryData.getCitiesOfState(this.userState)
            this.userCity = ''
        },

        updateLocation(){
            
            let userRef = fb.usersCollection.doc(this.$store.state.currentUser.uid)

            this.loading = true

            let userCountry = countryData.getCountryByCode(this.userCountry)
            let userState = countryData.getStateById(this.userState)
            let userCity = countryData.getCityById(this.userCity)

            let updates = {
                location: {
                    'country': userCountry,
                    'state': userState,
                    'city': userCity
                }
            }

            userRef.update(updates).then(() => {
                
                this.location = updates.location

                this.loading = false
                this.changeLocationInit = null

            }).catch(err => {

                this.loading = false
                this.changeLocationInit = null

                console.log('Error updating location: ', err.message)
            })
        },
        toggleLocation(){
            this.changeLocationInit = true
        }
    }
}
</script>


// WEBPACK FOOTER //
// src/views/Account/Settings.vue